/** Notifications + Activity + Exports + Integrations + Invoice print. */
import { api } from "@/lib/api";
import type { Page } from "@/lib/api-cadastros";

// ── Notifications ─────────────────────────────────────────────────────────
export type NotificationKind =
  | "job.created"
  | "job.assigned"
  | "job.completed"
  | "job.approved"
  | "invoice.created"
  | "invoice.approved"
  | "invoice.paid"
  | "broadcast.dispatched"
  | "comment.added"
  | "review.received"
  | "system";

export interface NotificationItem {
  id: string;
  user_id: string;
  kind: NotificationKind;
  title: string;
  body: string | null;
  link: string | null;
  icon: string | null;
  payload: Record<string, unknown> | null;
  read_at: string | null;
  is_archived: boolean;
  created_at: string;
}

export const notificationsApi = {
  list: async (params: { only_unread?: boolean; size?: number } = {}) => {
    const { data } = await api.get<Page<NotificationItem>>("/v1/notifications", { params });
    return data;
  },
  unreadCount: async (): Promise<{ unread: number }> => {
    const { data } = await api.get<{ unread: number }>("/v1/notifications/unread-count");
    return data;
  },
  markRead: async (id: string): Promise<NotificationItem> => {
    const { data } = await api.post<NotificationItem>(`/v1/notifications/${id}/read`);
    return data;
  },
  markAllRead: async () => {
    const { data } = await api.post<{ marked_read: number; at: string }>("/v1/notifications/mark-all-read");
    return data;
  },
  archive: async (id: string): Promise<void> => {
    await api.post(`/v1/notifications/${id}/archive`);
  },
};

// ── Activity log ──────────────────────────────────────────────────────────
export interface ActivityEntry {
  id: string;
  actor_id: string | null;
  actor_email: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  summary: string | null;
  metadata_json: Record<string, unknown> | null;
  created_at: string;
}

export const activityApi = {
  list: async (params: Record<string, unknown> = {}): Promise<Page<ActivityEntry>> => {
    const { data } = await api.get<Page<ActivityEntry>>("/v1/activity", { params });
    return data;
  },
};

// ── Exports ───────────────────────────────────────────────────────────────
export function exportUrl(resource: "jobs" | "clients" | "nerds" | "invoices" | "users"): string {
  const base = api.defaults.baseURL ?? "";
  return `${base}/v1/exports/${resource}.csv`;
}

export async function downloadCsv(resource: "jobs" | "clients" | "nerds" | "invoices" | "users") {
  const { data } = await api.get(`/v1/exports/${resource}.csv`, { responseType: "blob" });
  const url = URL.createObjectURL(data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${resource}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

// ── API Keys + Webhooks ───────────────────────────────────────────────────
export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  is_active: boolean;
  created_at: string;
}

export interface ApiKeyCreated extends ApiKey {
  raw_key: string;
}

export const apiKeysApi = {
  list: async (): Promise<Page<ApiKey>> => {
    const { data } = await api.get<Page<ApiKey>>("/v1/api-keys", { params: { size: 100 } });
    return data;
  },
  create: async (body: { name: string; scopes?: string[]; expires_at?: string | null }): Promise<ApiKeyCreated> => {
    const { data } = await api.post<ApiKeyCreated>("/v1/api-keys", body);
    return data;
  },
  revoke: async (id: string): Promise<ApiKey> => {
    const { data } = await api.post<ApiKey>(`/v1/api-keys/${id}/revoke`);
    return data;
  },
  remove: async (id: string): Promise<void> => {
    await api.delete(`/v1/api-keys/${id}`);
  },
};

export interface Webhook {
  id: string;
  name: string;
  url: string;
  events: string[];
  is_active: boolean;
  failure_count: number;
  last_success_at: string | null;
  last_failure_at: string | null;
  created_at: string;
}

export interface WebhookCreated extends Webhook {
  secret: string;
}

export const webhooksApi = {
  list: async (): Promise<Page<Webhook>> => {
    const { data } = await api.get<Page<Webhook>>("/v1/webhooks", { params: { size: 100 } });
    return data;
  },
  create: async (body: { name: string; url: string; events: string[]; headers?: Record<string, string> }): Promise<WebhookCreated> => {
    const { data } = await api.post<WebhookCreated>("/v1/webhooks", body);
    return data;
  },
  remove: async (id: string): Promise<void> => {
    await api.delete(`/v1/webhooks/${id}`);
  },
  test: async (id: string) => {
    const { data } = await api.post<{ status: number; ok: boolean; body?: string; error?: string }>(`/v1/webhooks/${id}/test`);
    return data;
  },
};

// ── Invoice print ─────────────────────────────────────────────────────────
export function invoicePrintUrl(invoiceId: string): string {
  const base = api.defaults.baseURL ?? "";
  // Pra usar com window.open() — abre em nova aba + auto-print
  return `${base}/v1/invoices/${invoiceId}/print`;
}
